// apps/web/src/components/brand-safety/BrandSafetyDashboard.tsx
// Brand safety management dashboard

import type React from 'react';
import { useState, useEffect } from 'react';
import type { BrandSafetyEvaluation } from '../../types/brand-safety';
import { apiClient } from '../../lib/apiClient';

export function BrandSafetyDashboard() {
  const [evaluations, setEvaluations] = useState<BrandSafetyEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const fetchEvaluations = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/brand-safety/evaluations');
        setEvaluations(response.data.evaluations);
      } catch (err) {
        setError('Failed to load brand safety evaluations');
        console.error('Error loading brand safety evaluations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluations();
  }, []);

  const handleCreateEvaluation = async (evaluationData: any) => {
    try {
      const response = await apiClient.post('/api/v1/brand-safety/evaluations', evaluationData);
      setEvaluations([response.data, ...evaluations]);
      setShowCreateForm(false);
    } catch (err) {
      setError('Failed to create evaluation');
      console.error('Error creating evaluation:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading brand safety data...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="brand-safety-dashboard">
      <div className="header">
        <h2>Brand Safety Dashboard</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn btn-primary"
        >
          {showCreateForm ? 'Cancel' : 'Create Evaluation'}
        </button>
      </div>

      {showCreateForm && (
        <BrandSafetyForm 
          onSubmit={handleCreateEvaluation} 
          onCancel={() => setShowCreateForm(false)} 
        />
      )}

      <div className="evaluations-grid">
        {evaluations.map(evaluation => (
          <EvaluationCard key={evaluation.id} evaluation={evaluation} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function EvaluationCard({ evaluation }: { evaluation: BrandSafetyEvaluation }) {
  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'LOW': return 'risk-low';
      case 'MEDIUM': return 'risk-medium';
      case 'HIGH': return 'risk-high';
      case 'CRITICAL': return 'risk-critical';
      default: return 'risk-default';
    }
  };

  return (
    <div className="evaluation-card">
      <div className="evaluation-header">
        <h3>{evaluation.title}</h3>
        <span className={`risk-badge ${getRiskLevelColor(evaluation.riskLevel)}`}>
          {evaluation.riskLevel}
        </span>
      </div>
      <p><strong>Subject:</strong> {evaluation.subjectType} - {evaluation.subjectId.substring(0, 8)}...</p>
      <p><strong>Policy:</strong> {evaluation.policyId?.substring(0, 8) ?? '—'}...</p>
      <p><strong>Date:</strong> {evaluation.evaluationDate}</p>
      <p><strong>Status:</strong> {evaluation.status}</p>
      <div className="actions">
        <button className="btn btn-secondary">View Details</button>
        <button className="btn btn-warning">Review</button>
        <button className="btn btn-danger">Acknowledge</button>
      </div>
    </div>
  );
}

// Helper component
function BrandSafetyForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    title: '',
    subjectType: 'CONTENT',
    subjectId: '',
    policyId: '',
    riskLevel: 'MEDIUM',
    description: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="brand-safety-form">
      <h3>Create Brand Safety Evaluation</h3>
      <input
        type="text"
        placeholder="Title"
        value={formData.title}
        onChange={(e) => setFormData({...formData, title: e.target.value})}
        required
      />
      <select
        value={formData.subjectType}
        onChange={(e) => setFormData({...formData, subjectType: e.target.value})}
      >
        <option value="CONTENT">Content</option>
        <option value="CREATOR">Creator</option>
        <option value="CAMPAIGN">Campaign</option>
        <option value="BRAND">Brand</option>
      </select>
      <input
        type="text"
        placeholder="Subject ID"
        value={formData.subjectId}
        onChange={(e) => setFormData({...formData, subjectId: e.target.value})}
        required
      />
      <input
        type="text"
        placeholder="Policy ID"
        value={formData.policyId}
        onChange={(e) => setFormData({...formData, policyId: e.target.value})}
        required
      />
      <select
        value={formData.riskLevel}
        onChange={(e) => setFormData({...formData, riskLevel: e.target.value})}
      >
        <option value="LOW">Low Risk</option>
        <option value="MEDIUM">Medium Risk</option>
        <option value="HIGH">High Risk</option>
        <option value="CRITICAL">Critical Risk</option>
      </select>
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create Evaluation</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}